import { useState, useEffect, useMemo } from 'react';
import { Select, TimePicker } from 'antd';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { LBNodeData, IntervalUnit } from '@/proto/logiblocks/logiblocks';
import { ChevronDownSmallIcon } from '@/assets/icons/icons';
import styles from '../NodeCustomization.module.css';
import snoozeStyles from './SnoozeActionCustomization.module.css';
import { CustomizationComponent } from '@/pages/Logiblocks/types';
import { FieldErrorLabel, FieldLabel } from '../shared';
import { SnoozeActionFields } from './SnoozeActionNodeInfo';
import { DEFAULT_SNOOZE_ACTION, DEFAULT_SNOOZE_DURATION } from './defaults';
import { getUserTimeZone } from '@/common/utils/utils';
import {
  durationToSeconds,
  formatTimezoneOffset,
  secondsToDuration,
  userSecondsToUtcSeconds,
  utcSecondsToUserSeconds,
} from '@/pages/Logiblocks/dateTimeUtils';

enum SnoozeType {
  DURATION = 'duration',
  UNTIL_TIME = 'until_time',
}

// Seconds since midnight to dayjs (for TimePicker)
const secondsToDayjs = (seconds: number): Dayjs =>
  dayjs().startOf('day').add(seconds, 'seconds');

// Dayjs to seconds since midnight (from TimePicker)
const dayjsToSeconds = (time: Dayjs): number =>
  time.hour() * 3600 + time.minute() * 60;

// Get default until_time (current time rounded to nearest 15 min, in UTC seconds since midnight)
const getDefaultUntilTimeUtc = (): number => {
  const now = new Date();
  const minutes = Math.round(now.getMinutes() / 15) * 15;
  now.setMinutes(minutes, 0, 0);
  return now.getUTCHours() * 3600 + now.getUTCMinutes() * 60;
};

// Determine snooze type based on which field is set
const getSnoozeType = (data: LBNodeData['snooze_action']): SnoozeType => {
  if (data?.until_time !== undefined) {
    return SnoozeType.UNTIL_TIME;
  }
  return SnoozeType.DURATION;
};

const SNOOZE_TYPE_OPTIONS = [
  { value: SnoozeType.DURATION, label: 'Snooze for' },
  { value: SnoozeType.UNTIL_TIME, label: 'Snooze until' },
];

// The design's select indicator: a 16px "chevron down small" in gray (#5E5E5E).
const SELECT_CHEVRON = <ChevronDownSmallIcon color='var(--text-secondary)' />;

const UNIT_OPTIONS = [
  { value: IntervalUnit.MINUTES, label: 'Minutes' },
  { value: IntervalUnit.HOURS, label: 'Hours' },
];

/**
 * The amount typed into the box as a number: 0 while it is empty or not a positive number, which
 * publish validation flags as required. The box keeps whatever was typed, so it can be cleared.
 */
const parseAmount = (text: string): number => {
  const amount = Number(text);
  return text.trim() !== '' && Number.isFinite(amount) && amount > 0
    ? amount
    : 0;
};

/**
 * Customization UI for SNOOZE_ACTION nodes.
 * Allows configuring how long the triggering alert stays snoozed: a duration with unit
 * selection (minutes/hours), or a specific time of day to snooze until.
 */
export const SnoozeActionCustomization: CustomizationComponent = ({
  nodeData,
  onChange,
  isReadOnly = false,
  validationErrors,
}) => {
  const snoozeDurationError = validationErrors?.find(
    (e) => e.field === SnoozeActionFields.SNOOZE_DURATION
  );
  const timeError = validationErrors?.find(
    (e) => e.field === SnoozeActionFields.TIME
  );
  const currentSnoozeData = nodeData.snooze_action || DEFAULT_SNOOZE_ACTION;

  const [snoozeType, setSnoozeType] = useState(() =>
    getSnoozeType(currentSnoozeData)
  );
  // What is in the Amount box, as text, so it can be emptied while typing. Fall back to
  // DEFAULT_SNOOZE_DURATION when the node is in UNTIL_TIME mode (no `duration` field).
  const [amountText, setAmountText] = useState(() =>
    String(
      secondsToDuration(
        currentSnoozeData.duration?.seconds ?? DEFAULT_SNOOZE_DURATION.seconds,
        currentSnoozeData.duration?.display_unit ??
          DEFAULT_SNOOZE_DURATION.display_unit
      )
    )
  );
  const [unit, setUnit] = useState(
    currentSnoozeData.duration?.display_unit ??
      DEFAULT_SNOOZE_DURATION.display_unit
  );
  // The time-of-day field keeps the user's cleared state as `undefined` so validation can flag
  // it. Seed a default only when the field doesn't exist yet (new node / mode-switch).
  const [untilTimeUtc, setUntilTimeUtc] = useState<number | undefined>(() =>
    currentSnoozeData.until_time
      ? currentSnoozeData.until_time.seconds_since_midnight
      : getDefaultUntilTimeUtc()
  );

  // Memoize timezone info to avoid recalculating on every render
  const timezoneInfo = useMemo(() => {
    const tz = getUserTimeZone();
    return {
      name: tz.name,
      displayString: `Timezone set to (${formatTimezoneOffset(tz.offsetMinutes)}) ${tz.name}`,
    };
  }, []);

  // Update local state when nodeData changes (e.g., node switching)
  useEffect(() => {
    const snoozeData = nodeData.snooze_action || DEFAULT_SNOOZE_ACTION;
    const newSnoozeType = getSnoozeType(snoozeData);
    setSnoozeType(newSnoozeType);

    switch (newSnoozeType) {
      case SnoozeType.UNTIL_TIME:
        setUntilTimeUtc(
          snoozeData.until_time
            ? snoozeData.until_time.seconds_since_midnight
            : getDefaultUntilTimeUtc()
        );
        break;
      case SnoozeType.DURATION:
      default: {
        const newSeconds =
          snoozeData.duration?.seconds ?? DEFAULT_SNOOZE_DURATION.seconds;
        const newUnit =
          snoozeData.duration?.display_unit ??
          DEFAULT_SNOOZE_DURATION.display_unit;
        setUnit(newUnit);
        // Only replace the box when the node's duration no longer matches what is typed. The
        // draft is echoed back after every keystroke, and rewriting the box then would undo
        // an emptied box or a half-typed number such as "0." or "0.123".
        setAmountText((typed) =>
          durationToSeconds(parseAmount(typed), newUnit) === newSeconds
            ? typed
            : String(secondsToDuration(newSeconds, newUnit))
        );
        break;
      }
    }
  }, [nodeData]);

  /**
   * Handles changes to the duration amount input.
   * Updates local state and propagates the change to the parent via onChange.
   */
  const handleAmountChange = (text: string) => {
    setAmountText(text);

    const updatedData: Partial<LBNodeData> = {
      snooze_action: {
        duration: {
          seconds: durationToSeconds(parseAmount(text), unit),
          display_unit: unit,
        },
      },
    };

    onChange(updatedData);
  };

  /**
   * Handles changes to the duration unit dropdown.
   * Updates local state and propagates the change to the parent via onChange.
   */
  const handleUnitChange = (newUnit: IntervalUnit) => {
    setUnit(newUnit);

    const updatedData: Partial<LBNodeData> = {
      snooze_action: {
        duration: {
          seconds: durationToSeconds(parseAmount(amountText), newUnit),
          display_unit: newUnit,
        },
      },
    };

    onChange(updatedData);
  };

  /**
   * Handles switching between snooze type modes.
   */
  const handleSnoozeTypeChange = (newType: SnoozeType) => {
    setSnoozeType(newType);

    let updatedData: Partial<LBNodeData>;
    switch (newType) {
      case SnoozeType.UNTIL_TIME:
        updatedData = {
          snooze_action: {
            until_time: {
              seconds_since_midnight: untilTimeUtc,
            },
          },
        };
        break;
      case SnoozeType.DURATION:
      default:
        updatedData = {
          snooze_action: {
            duration: {
              seconds: durationToSeconds(parseAmount(amountText), unit),
              display_unit: unit,
            },
          },
        };
        break;
    }
    onChange(updatedData);
  };

  /**
   * Handles time picker changes for "Snooze until" mode.
   * A null value means the user cleared the picker — propagate as undefined so validation flags it.
   */
  const handleUntilTimeChange = (timeValue: Dayjs | null) => {
    const utcSeconds = timeValue
      ? userSecondsToUtcSeconds(dayjsToSeconds(timeValue), timezoneInfo.name)
      : undefined;
    setUntilTimeUtc(utcSeconds);

    onChange({
      snooze_action: {
        until_time: {
          seconds_since_midnight: utcSeconds,
        },
      },
    });
  };

  // Convert UTC seconds to a Dayjs in the user's timezone for the TimePicker display.
  // null when the user has cleared the time so the picker shows its placeholder.
  const untilTimeDayjs =
    untilTimeUtc === undefined
      ? null
      : secondsToDayjs(
          utcSecondsToUserSeconds(untilTimeUtc, timezoneInfo.name)
        );

  return (
    <>
      <div className={styles.customizationSection}>
        <label className={styles.sectionLabel}>Type</label>
        <Select
          value={snoozeType}
          onChange={handleSnoozeTypeChange}
          disabled={isReadOnly}
          style={{ width: '100%' }}
          className='agentSelect agentSelect--darkText'
          suffixIcon={SELECT_CHEVRON}
        >
          {SNOOZE_TYPE_OPTIONS.map((opt) => (
            <Select.Option key={opt.value} value={opt.value}>
              {opt.label}
            </Select.Option>
          ))}
        </Select>
      </div>

      {snoozeType === SnoozeType.DURATION && (
        <div className={snoozeStyles.durationRow}>
          <div
            className={`${styles.customizationSection} ${snoozeStyles.durationField}`}
          >
            <FieldLabel required className={styles.sectionLabel}>
              Amount
            </FieldLabel>
            <input
              type='number'
              // The engine counts whole minutes, so minutes start at 1 and hours may be
              // fractional. There is no upper limit.
              min={unit === IntervalUnit.HOURS ? 0.1 : 1}
              step={unit === IntervalUnit.HOURS ? 'any' : 1}
              value={amountText}
              onChange={(e) => handleAmountChange(e.target.value)}
              className={`${styles.lbNumberInput} ${snoozeStyles.amountInput}`}
              disabled={isReadOnly}
            />
            <FieldErrorLabel error={snoozeDurationError} />
          </div>

          <div
            className={`${styles.customizationSection} ${snoozeStyles.durationField}`}
          >
            <label className={styles.sectionLabel}>Unit</label>
            <Select
              value={unit}
              onChange={handleUnitChange}
              disabled={isReadOnly}
              style={{ width: '100%' }}
              className='agentSelect agentSelect--darkText'
              suffixIcon={SELECT_CHEVRON}
            >
              {UNIT_OPTIONS.map((opt) => (
                <Select.Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Select.Option>
              ))}
            </Select>
          </div>
        </div>
      )}

      {snoozeType === SnoozeType.UNTIL_TIME && (
        <div className={styles.customizationSection}>
          <FieldLabel required className={styles.sectionLabel}>
            At
          </FieldLabel>
          <TimePicker
            value={untilTimeDayjs}
            onChange={handleUntilTimeChange}
            format='h:mm A'
            use12Hours
            disabled={isReadOnly}
            className={styles.lbTimePicker}
          />
          <FieldErrorLabel error={timeError} />
          <span className={snoozeStyles.timezoneNote}>
            {timezoneInfo.displayString}
          </span>
        </div>
      )}
    </>
  );
};
